import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    Box,
    Typography,
    IconButton,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress
} from '@mui/material';
import { RemoveCircleOutline, CloseOutlined } from '@mui/icons-material';

import { debitEmployeeBalanceApi } from 'utils/API/Mis Apis/HrPortelApis';
import { handleCatchError } from 'utils/CatchHandler';
import { globalNotifyError } from 'views/utilities/AlertToast';
import { formatINR } from './creditLimitsUtils';

const DebitBalanceModal = ({ open, onClose, account, onSuccess }) => {
    const [debitBalanceForm, setDebitBalanceForm] = useState({
        cross_user_id: '',
        amount: '',
        category: 'TA Deduction',
        custom_category: '',
        remarks: ''
    });
    const [isDebitingBalance, setIsDebitingBalance] = useState(false);

    useEffect(() => {
        if (account && open) {
            setDebitBalanceForm({
                cross_user_id: account?.cross_user_id ?? account?.crossbanking_user_id ?? account?.id ?? '',
                amount: '',
                category: 'TA Deduction',
                custom_category: '',
                remarks: ''
            });
        }
    }, [account, open]);

    const handleDebitBalanceSubmit = async (e) => {
        e.preventDefault();
        const crossUserId = parseInt(debitBalanceForm.cross_user_id || account?.cross_user_id || account?.id, 10);
        if (!crossUserId || isNaN(crossUserId)) {
            globalNotifyError('Unable to identify employee user ID');
            return;
        }

        const amount = parseFloat(debitBalanceForm.amount);
        if (isNaN(amount) || amount <= 0) {
            globalNotifyError('Please enter a valid amount greater than 0');
            return;
        }

        setIsDebitingBalance(true);
        try {
            const chosenCategory =
                debitBalanceForm.category === 'Other' ? debitBalanceForm.custom_category?.trim() || 'Other' : debitBalanceForm.category;

            const payload = {
                cross_user_id: crossUserId,
                amount: Number(amount),
                ...(chosenCategory && { category: chosenCategory }),
                ...(debitBalanceForm.remarks?.trim() && { remarks: debitBalanceForm.remarks.trim() })
            };

            const response = await debitEmployeeBalanceApi(payload);

            if (response?.data?.success) {
                onClose();
                if (onSuccess) onSuccess();
            } else if (response?.data?.message) {
            } else {
                onClose();
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error debiting employee balance:', error);
            if (error?.response?.data?.message) {
            } else {
                handleCatchError(error);
            }
        } finally {
            setIsDebitingBalance(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !isDebitingBalance && onClose()}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#fef2f2', color: '#dc2626', display: 'flex' }}>
                        <RemoveCircleOutline fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Debit Wallet Balance
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Deduct funds from employee Crossbanking wallet
                        </Typography>
                    </Box>
                </Stack>
                <IconButton size="small" onClick={onClose} disabled={isDebitingBalance}>
                    <CloseOutlined fontSize="small" />
                </IconButton>
            </DialogTitle>

            <form onSubmit={handleDebitBalanceSubmit}>
                <DialogContent sx={{ pt: 2 }}>
                    <Stack spacing={2.5}>
                        {/* Employee Info Card */}
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: '#fef2f2',
                                border: '1px solid',
                                borderColor: '#fecaca'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" display="block">
                                Employee Account
                            </Typography>
                            <Typography variant="subtitle1" fontWeight={700} color="#991b1b">
                                {account?.employee_name || account?.name || account?.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {account?.email}
                            </Typography>
                            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">
                                        Current Balance
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700} sx={{ color: '#dc2626' }}>
                                        {formatINR(account?.crossbanking_balance ?? 0)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>

                        {/* Amount */}
                        <TextField
                            label="Amount to Debit (₹)"
                            required
                            fullWidth
                            type="number"
                            value={debitBalanceForm.amount}
                            onChange={(e) => setDebitBalanceForm({ ...debitBalanceForm, amount: e.target.value })}
                            placeholder="e.g. 1000"
                            InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>
                            }}
                            helperText="Amount must be greater than 0"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />

                        {/* Category */}
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={debitBalanceForm.category}
                                label="Category"
                                onChange={(e) => setDebitBalanceForm({ ...debitBalanceForm, category: e.target.value })}
                                sx={{ borderRadius: 2 }}
                            >
                                <MenuItem value="TA Deduction">TA Deduction</MenuItem>
                                <MenuItem value="Excess Allowance Correction">Excess Allowance Correction</MenuItem>
                                <MenuItem value="Advance Recovery">Advance Recovery</MenuItem>
                                <MenuItem value="Penalty">Penalty</MenuItem>
                                <MenuItem value="Adjustment">Adjustment</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Custom Category if 'Other' */}
                        {debitBalanceForm.category === 'Other' && (
                            <TextField
                                label="Specify Deduction Reason"
                                fullWidth
                                value={debitBalanceForm.custom_category}
                                onChange={(e) => setDebitBalanceForm({ ...debitBalanceForm, custom_category: e.target.value })}
                                placeholder="e.g. Audit Recovery"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                        )}

                        {/* Remarks */}
                        <TextField
                            label="Remarks"
                            fullWidth
                            multiline
                            rows={2}
                            value={debitBalanceForm.remarks}
                            onChange={(e) => setDebitBalanceForm({ ...debitBalanceForm, remarks: e.target.value })}
                            placeholder="e.g. Excess travel allowance correction"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} disabled={isDebitingBalance} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={isDebitingBalance || !debitBalanceForm.amount}
                        startIcon={isDebitingBalance ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            px: 3,
                            bgcolor: '#dc2626',
                            '&:hover': { bgcolor: '#b91c1c' }
                        }}
                    >
                        {isDebitingBalance ? 'Debiting...' : 'Debit Balance'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

DebitBalanceModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    account: PropTypes.object,
    onSuccess: PropTypes.func
};

export default DebitBalanceModal;
